import { useEffect, useState, useRef } from "react";
import { Send, MessageSquare, Loader2, RefreshCw, Trash2, Copy, AlertTriangle, CheckCircle2, Clock, ExternalLink, Bot, User as UserIcon, Circle, UserPlus } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

function StatusBadge({ status, error }) {
  if (status === "sent") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-mono text-[10px] uppercase tracking-widest">
        <CheckCircle2 className="h-3 w-3 mr-1" /> Inviato
      </Badge>
    );
  }
  const label =
    error === "dm_closed" ? "DM Chiusi" :
    error === "user_not_found" ? "Utente non trovato" :
    error === "bot_not_running" ? "Bot offline" :
    error === "bot_not_ready" ? "Bot non pronto" :
    "Errore";
  return (
    <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/40 font-mono text-[10px] uppercase tracking-widest">
      <AlertTriangle className="h-3 w-3 mr-1" /> {label}
    </Badge>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function ChatBubble({ msg }) {
  const isOutbound = msg.direction === "outbound";
  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"} mb-3`} data-testid={`chat-bubble-${msg.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2 ${
        isOutbound
          ? "bg-sheriff-gold/15 border border-sheriff-gold/40 text-[#F5E6B8]"
          : "bg-[#2B2D31] border border-white/10 text-[#DBDEE1]"
      } ${msg.failed ? "opacity-60" : ""}`}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1">
          {isOutbound ? <Bot className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
          <span className="font-mono font-bold">{msg.author_display}</span>
          <span>·</span>
          <span>{fmtDate(msg.received_at)}</span>
          {msg.failed && <span className="text-rose-400">· FALLITO</span>}
        </div>
        <div className="text-sm whitespace-pre-line break-words">{msg.content}</div>
        {msg.attachments?.length > 0 && (
          <div className="mt-2 space-y-1">
            {msg.attachments.map((a, i) => (
              <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block text-xs text-[#00A8FC] underline break-all">
                📎 {a.filename}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FriendRequestAssistant({ selected }) {
  const inviteMsg = (
    `Ciao! Sono ${selected.sent_by} del Los Santos County Sheriff's Department.\n\n` +
    `${selected.custom_message ? selected.custom_message + "\n\n" : ""}` +
    `Ti ho selezionato come possibile candidato per entrare nel dipartimento. ` +
    `Puoi candidarti qui: ${selected.invite_link}`
  );

  const copyMsg = () => {
    navigator.clipboard.writeText(inviteMsg);
    toast.success("Messaggio copiato — incollalo su Discord dopo aver aperto il DM");
  };
  const openDiscordProfile = () => {
    // Prova prima il deep-link app, fallback al web
    const appUrl = `discord://-/users/${selected.discord_id}`;
    const webUrl = `https://discord.com/users/${selected.discord_id}`;
    window.location.href = appUrl;
    // Fallback se l'app non si apre
    setTimeout(() => { window.open(webUrl, "_blank"); }, 800);
  };
  const copyId = () => {
    navigator.clipboard.writeText(selected.discord_id);
    toast.success("Discord ID copiato");
  };

  return (
    <div className="px-5 py-4 border-b border-sheriff-gold/20 bg-amber-500/5" data-testid="friend-request-assistant">
      <div className="flex items-start gap-3">
        <UserPlus className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-200 mb-1">L&apos;utente ha i DM chiusi</div>
          <p className="text-xs text-amber-200/80 leading-relaxed mb-3">
            Il bot non può inviare richieste d&apos;amicizia (limitazione dell&apos;API Discord).
            Contattalo <span className="font-semibold">tu personalmente</span>: aggiungilo come
            amico e incolla il messaggio precompilato qui sotto.
          </p>
          <div className="rounded-lg bg-[#0A1F18] border border-sheriff-gold/20 p-3 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-sheriff-gold/60 font-mono mb-1">Messaggio precompilato</div>
            <div className="text-xs text-[#DBDEE1] whitespace-pre-line font-mono">{inviteMsg}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={openDiscordProfile}
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
              data-testid="fr-open-profile">
              <UserPlus className="h-3 w-3 mr-1" /> Apri profilo su Discord
            </Button>
            <Button size="sm" variant="outline" onClick={copyMsg}
              className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10"
              data-testid="fr-copy-msg">
              <Copy className="h-3 w-3 mr-1" /> Copia messaggio
            </Button>
            <Button size="sm" variant="outline" onClick={copyId}
              className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10"
              data-testid="fr-copy-id">
              <Copy className="h-3 w-3 mr-1" /> Copia ID
            </Button>
          </div>
          <div className="text-[10px] text-sheriff-gold/50 mt-3 font-mono">
            💡 Il pulsante &quot;Apri profilo&quot; tenta di lanciare l&apos;app Discord; se non
            si apre entro un secondo, apre automaticamente la versione web.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DiscordInvitesPanel() {
  const [discordId, setDiscordId] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);
  const chatBottomRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/executive/discord-invites");
      setInvites(data || []);
    } catch (e) {
      toast.error("Impossibile caricare lo storico inviti");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (inviteId) => {
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/executive/discord-invites/${inviteId}/messages`);
      setMessages(data.messages || []);
      // Aggiorna il badge unread in locale
      setInvites((prev) => prev.map((i) => i.id === inviteId ? { ...i, has_unread_reply: false } : i));
    } catch (e) {
      toast.error("Impossibile caricare la chat");
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Polling ogni 15s dello storico (per intercettare nuove risposte)
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  // Quando cambia l'invito selezionato, carica i messaggi
  useEffect(() => {
    if (selected?.id) loadMessages(selected.id);
    else setMessages([]);
  }, [selected?.id]);

  // Auto scroll al bottom quando arrivano nuovi messaggi
  useEffect(() => {
    if (chatBottomRef.current) chatBottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const send = async () => {
    const id = discordId.replace(/\D/g, "").trim();
    if (!id || id.length < 6) {
      toast.error("Discord ID non valido (min 6 cifre)");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/executive/discord-invites", {
        discord_id: id,
        custom_message: customMessage.trim(),
      });
      if (data.ok) {
        toast.success(`Invito inviato a ${data.invite.discord_username || id}`);
        setDiscordId("");
        setCustomMessage("");
      } else {
        const reason = data.invite?.error || "sconosciuto";
        toast.error(`Invio fallito: ${reason}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setReplying(true);
    try {
      await api.post(`/executive/discord-invites/${selected.id}/reply`, { content: reply.trim() });
      toast.success("Risposta inviata");
      setReply("");
      await loadMessages(selected.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore risposta");
    } finally {
      setReplying(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo invito e tutta la conversazione dallo storico? Il DM già inviato NON verrà ritirato.")) return;
    try {
      await api.delete(`/executive/discord-invites/${id}`);
      toast.success("Invito rimosso");
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiato");
  };

  return (
    <div className="space-y-6" data-testid="discord-invites-panel">
      {/* SEND FORM */}
      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-6" data-testid="discord-invite-form">
        <div className="flex items-center gap-2 mb-4">
          <Send className="h-4 w-4 text-sheriff-gold" />
          <h3 className="font-heading font-bold text-lg text-[#F5E6B8]">Invia invito candidatura</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="text-xs uppercase tracking-widest text-sheriff-gold/70 font-mono block mb-2">
              Discord ID destinatario
            </label>
            <Input
              value={discordId}
              onChange={(e) => setDiscordId(e.target.value.replace(/\D/g, ""))}
              placeholder="es. 987654321012345678"
              maxLength={22}
              data-testid="discord-invite-id"
              className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40 font-mono"
            />
            <p className="text-[10px] text-sheriff-gold/40 mt-1 font-mono">
              Solo cifre. Attiva &quot;modalità sviluppatore&quot; su Discord per copiarlo dal profilo.
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-sheriff-gold/70 font-mono block mb-2">
              Messaggio personale (facoltativo)
            </label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              maxLength={800}
              placeholder='Ciao! Ho visto il tuo profilo su [server] e mi sembri un buon candidato. Ti aspetto in candidatura.'
              data-testid="discord-invite-message"
              className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
            />
            <div className="text-[10px] text-sheriff-gold/40 mt-1 text-right font-mono">
              {customMessage.length}/800
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          <div className="text-xs text-sheriff-gold/50 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Il bot LSSD Administrator invierà un embed con link cliccabile
          </div>
          <Button
            onClick={send}
            disabled={sending || !discordId}
            data-testid="discord-invite-send"
            className="bg-sheriff-gold text-[#0A1F18] hover:bg-sheriff-gold/90 font-bold"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Invia Invito
          </Button>
        </div>
      </div>

      {/* CONVERSAZIONI */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LISTA INVITI */}
        <div className="lg:col-span-2 bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl overflow-hidden" data-testid="discord-invites-history">
          <div className="flex items-center justify-between px-5 py-3 border-b border-sheriff-gold/20">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-sheriff-gold" />
              <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-sheriff-gold">
                Conversazioni ({invites.length})
              </h3>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
              className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full"
              data-testid="discord-invites-refresh">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-sheriff-gold/10">
            {loading && invites.length === 0 ? (
              <div className="p-6 text-sheriff-gold/60 text-sm flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
              </div>
            ) : invites.length === 0 ? (
              <div className="p-8 text-sheriff-gold/40 text-sm text-center font-mono">
                Nessun invito ancora
              </div>
            ) : (
              invites.map((inv) => (
                <button
                  key={inv.id}
                  onClick={() => setSelected(inv)}
                  data-testid={`invite-row-${inv.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-sheriff-gold/5 transition-colors ${
                    selected?.id === inv.id ? "bg-sheriff-gold/10 border-l-2 border-l-sheriff-gold" : "border-l-2 border-l-transparent"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {inv.has_unread_reply && (
                      <Circle className="h-2 w-2 text-emerald-400 fill-emerald-400 shrink-0 mt-1.5 animate-pulse" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-[#F5E6B8] font-semibold text-sm truncate">
                          {inv.discord_username || `ID: ${inv.discord_id}`}
                        </div>
                        {inv.has_unread_reply && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[9px] uppercase tracking-widest">
                            Nuovo
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-sheriff-gold/60 mt-1 font-mono flex items-center gap-2 flex-wrap">
                        <StatusBadge status={inv.status} error={inv.error} />
                        <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{fmtDate(inv.last_reply_at || inv.sent_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* CHAT VIEW */}
        <div className="lg:col-span-3 bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl overflow-hidden flex flex-col" data-testid="discord-invite-chat" style={{ maxHeight: 700, minHeight: 500 }}>
          {!selected ? (
            <div className="p-8 text-sheriff-gold/40 text-sm text-center font-mono flex-1 flex items-center justify-center">
              Seleziona una conversazione dalla lista
            </div>
          ) : (
            <>
              {/* HEADER */}
              <div className="px-5 py-3 border-b border-sheriff-gold/20 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-[#F5E6B8] font-semibold text-sm">
                      {selected.discord_username || `ID: ${selected.discord_id}`}
                    </div>
                    <StatusBadge status={selected.status} error={selected.error} />
                  </div>
                  <div className="text-[10px] text-sheriff-gold/50 font-mono">
                    ID: {selected.discord_id} · Inviato {fmtDate(selected.sent_at)} da {selected.sent_by}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => loadMessages(selected.id)} disabled={loadingMsgs}
                    className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full"
                    data-testid="chat-refresh">
                    {loadingMsgs ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyLink(selected.invite_link)}
                    className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full"
                    title="Copia link">
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(selected.invite_link, "_blank")}
                    className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full"
                    title="Apri link">
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(selected.id)}
                    className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-full"
                    data-testid="invite-delete"
                    title="Elimina">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* FRIEND REQUEST ASSISTANT — quando DM chiusi */}
              {selected.status === "failed" && selected.error === "dm_closed" && (
                <FriendRequestAssistant selected={selected} />
              )}

              {/* MESSAGGI */}
              <div className="flex-1 overflow-y-auto p-4 bg-[#0A1F18]/60" data-testid="chat-messages">
                {loadingMsgs ? (
                  <div className="text-sheriff-gold/60 text-sm flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Caricamento messaggi…
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-sheriff-gold/40 text-sm text-center font-mono py-8">
                    Nessun messaggio nella conversazione
                  </div>
                ) : (
                  messages.map((m) => <ChatBubble key={m.id} msg={m} />)
                )}
                <div ref={chatBottomRef} />
              </div>

              {/* REPLY BOX */}
              <div className="border-t border-sheriff-gold/20 p-3 bg-[#0F2620]">
                <div className="flex gap-2 items-end">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReply(); }
                    }}
                    rows={2}
                    maxLength={2000}
                    placeholder="Scrivi una risposta… (Ctrl+Enter per inviare)"
                    data-testid="chat-reply-input"
                    className="flex-1 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40 resize-none"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={replying || !reply.trim()}
                    data-testid="chat-reply-send"
                    className="bg-sheriff-gold text-[#0A1F18] hover:bg-sheriff-gold/90 font-bold shrink-0 h-full min-h-[60px]"
                  >
                    {replying ? <Loader2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="text-[9px] text-sheriff-gold/40 mt-1 font-mono text-right">
                  {reply.length}/2000
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
